import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Calendar,
  Shield,
  Lock,
  Eye,
  EyeOff,
  Crown,
  CreditCard,
  FileText,
  Trash2,
  Check,
  X,
  AlertTriangle,
  Loader2,
  ChevronRight,
  Download,
  Plus,
  Edit3,
  Save,
  HelpCircle,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccountPage() {
  const { user, setUser, isPremium, logout, tutorialsEnabled, setTutorialsEnabled, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [updatingTutorials, setUpdatingTutorials] = useState(false);
  
  // Edit states
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(false);
  
  // Password states
  const [changingPassword, setChangingPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Delete account states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/account`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAccountData(data);
        setEditName(data.name || '');
        setEditEmail(data.email || '');
        setUsage(data.usage);
      }
    } catch (error) {
      console.error('Error fetching account:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    setProfileError(null);
    setProfileSuccess(false);
    setProfileSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/account/update`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ name: editName, email: editEmail })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la mise à jour');
      }
      
      setAccountData({ ...accountData, ...data });
      // Create a fresh clean object to avoid cloning issues on Safari
      setUser({
        user_id: user.user_id,
        email: data.email,
        name: data.name,
        picture: user.picture,
        subscription_plan: user.subscription_plan,
        terms_accepted: user.terms_accepted,
        tutorials_enabled: user.tutorials_enabled,
        usage: user.usage
      });
      setProfileSuccess(true);
      setEditingProfile(false);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setPasswordSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/account/password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ 
          old_password: oldPassword, 
          new_password: newPassword 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors du changement de mot de passe');
      }
      
      setPasswordSuccess(true);
      setChangingPassword(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      setDeleteError('Veuillez taper SUPPRIMER pour confirmer');
      return;
    }
    
    setDeleteError(null);
    setDeleting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/account/delete`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ password: deletePassword })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la suppression');
      }
      
      logout();
      navigate('/');
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Non disponible';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black-rich pt-24 pb-12 px-6 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-gold-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black-rich pt-24 pb-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-2">
            Espace client
          </h1>
          <p className="text-zinc-400">
            Gérez vos informations personnelles, votre abonnement et vos préférences
          </p>
        </motion.div>

        <div className="space-y-6">
          {/* ==================== INFORMATIONS DU COMPTE ==================== */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gold-500/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-gold-500" />
                </div>
                <div>
                  <h2 className="text-xl font-heading font-bold text-white">Informations du compte</h2>
                  <p className="text-zinc-500 text-sm">Vos données personnelles</p>
                </div>
              </div>
              {!editingProfile && (
                <Button
                  onClick={() => setEditingProfile(true)}
                  variant="outline"
                  className="border-gold-500/30 text-gold-500 hover:bg-gold-500/10"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              )}
            </div>

            {profileSuccess && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-500" />
                <p className="text-emerald-400 text-sm">Informations mises à jour avec succès</p>
              </div>
            )}

            {profileError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">{profileError}</p>
              </div>
            )}

            {editingProfile ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Prénom ou surnom</label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Email</label>
                  <Input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleUpdateProfile}
                    disabled={profileSaving}
                    className="bg-gold-500 hover:bg-gold-400 text-black"
                  >
                    {profileSaving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingProfile(false);
                      setEditName(accountData?.name || '');
                      setEditEmail(accountData?.email || '');
                      setProfileError(null);
                    }}
                    variant="outline"
                    className="border-zinc-700 text-zinc-400"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-zinc-800/30 rounded-xl">
                  <User className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-zinc-500 text-sm">Prénom ou surnom</p>
                    <p className="text-white font-medium">{accountData?.name || 'Non défini'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-zinc-800/30 rounded-xl">
                  <Mail className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-zinc-500 text-sm">Email</p>
                    <p className="text-white font-medium">{accountData?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-zinc-800/30 rounded-xl">
                  <Calendar className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-zinc-500 text-sm">Membre depuis</p>
                    <p className="text-white font-medium">{formatDate(accountData?.created_at)}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* ==================== SÉCURITÉ ==================== */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-white">Sécurité du compte</h2>
                <p className="text-zinc-500 text-sm">Gérez votre mot de passe</p>
              </div>
            </div>

            {passwordSuccess && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-500" />
                <p className="text-emerald-400 text-sm">Mot de passe modifié avec succès</p>
              </div>
            )}

            {!changingPassword ? (
              <Button
                onClick={() => setChangingPassword(true)}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Lock className="w-4 h-4 mr-2" />
                Changer le mot de passe
              </Button>
            ) : (
              <div className="space-y-4">
                {passwordError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm">{passwordError}</p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Ancien mot de passe</label>
                  <div className="relative">
                    <Input
                      type={showOldPassword ? 'text' : 'password'}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className="bg-zinc-800/50 border-zinc-700 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
                    >
                      {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Nouveau mot de passe</label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-zinc-800/50 border-zinc-700 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Confirmer le nouveau mot de passe</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleChangePassword}
                    disabled={passwordSaving}
                    className="bg-gold-500 hover:bg-gold-400 text-black"
                  >
                    {passwordSaving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer
                  </Button>
                  <Button
                    onClick={() => {
                      setChangingPassword(false);
                      setOldPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError(null);
                    }}
                    variant="outline"
                    className="border-zinc-700 text-zinc-400"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </motion.div>

          {/* ==================== ABONNEMENT ==================== */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPremium ? 'bg-gold-500/20' : 'bg-zinc-700/50'}`}>
                <Crown className={`w-6 h-6 ${isPremium ? 'text-gold-500' : 'text-zinc-500'}`} />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-white">Mon abonnement</h2>
                <p className="text-zinc-500 text-sm">Votre plan et utilisation</p>
              </div>
            </div>

            {/* Status Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isPremium 
                ? 'bg-gold-500/20 text-gold-500 border border-gold-500/30' 
                : 'bg-zinc-700/50 text-zinc-400 border border-zinc-600'
            }`}>
              <Crown className="w-4 h-4" />
              <span className="font-bold">{isPremium ? 'Premium' : 'Gratuit'}</span>
            </div>

            {/* Usage Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-zinc-800/30 rounded-xl text-center">
                <p className="text-3xl font-bold text-white">{usage?.quotes_analyzed || 0}</p>
                <p className="text-zinc-500 text-sm">Audits de devis</p>
                {!isPremium && <p className="text-xs text-zinc-600 mt-1">Limite: 1</p>}
              </div>
              <div className="p-4 bg-zinc-800/30 rounded-xl text-center">
                <p className="text-3xl font-bold text-white">{usage?.estimates_created || 0}</p>
                <p className="text-zinc-500 text-sm">Budgets travaux</p>
                {!isPremium && <p className="text-xs text-zinc-600 mt-1">Limite: 1</p>}
              </div>
              <div className="p-4 bg-zinc-800/30 rounded-xl text-center">
                <p className="text-3xl font-bold text-white">{usage?.recipes_created || 0}</p>
                <p className="text-zinc-500 text-sm">Recettes travaux</p>
                {!isPremium && <p className="text-xs text-zinc-600 mt-1">Limite: 2</p>}
              </div>
            </div>

            {isPremium && accountData?.next_billing_date && (
              <div className="p-4 bg-gold-500/10 border border-gold-500/20 rounded-xl mb-6">
                <p className="text-zinc-400 text-sm">Prochaine facturation</p>
                <p className="text-gold-500 font-bold">{formatDate(accountData.next_billing_date)} — 19,00 €</p>
              </div>
            )}

            {!isPremium && (
              <div className="space-y-2">
                <Button
                  onClick={() => navigate('/subscription')}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-black font-bold"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Passer à Premium — 19€/mois
                </Button>
                <p className="text-zinc-500 text-xs text-center">
                  Sans engagement — résiliable à tout moment
                </p>
              </div>
            )}
          </motion.div>

          {/* ==================== MOYEN DE PAIEMENT ==================== */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-white">Moyen de paiement</h2>
                <p className="text-zinc-500 text-sm">Gérez vos cartes bancaires</p>
              </div>
            </div>

            {accountData?.payment_method ? (
              <div className="flex items-center justify-between p-4 bg-zinc-800/30 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">VISA</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">•••• •••• •••• {accountData.payment_method.last4}</p>
                    <p className="text-zinc-500 text-sm">Expire {accountData.payment_method.exp_month}/{accountData.payment_method.exp_year}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500 mb-4">Aucun moyen de paiement enregistré</p>
                <Button
                  onClick={() => navigate('/subscription')}
                  variant="outline"
                  className="border-gold-500/30 text-gold-500 hover:bg-gold-500/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une carte
                </Button>
              </div>
            )}
          </motion.div>

          {/* ==================== FACTURES ==================== */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-white">Mes factures</h2>
                <p className="text-zinc-500 text-sm">Historique de facturation</p>
              </div>
            </div>

            {accountData?.invoices && accountData.invoices.length > 0 ? (
              <div className="space-y-3">
                {accountData.invoices.map((invoice, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-zinc-800/30 rounded-xl">
                    <div className="flex items-center gap-4">
                      <FileText className="w-5 h-5 text-zinc-500" />
                      <div>
                        <p className="text-white font-medium">Facture #{invoice.number}</p>
                        <p className="text-zinc-500 text-sm">{formatDate(invoice.date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-gold-500 font-bold">{invoice.amount} €</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500">Aucune facture disponible</p>
              </div>
            )}
          </motion.div>

          {/* ==================== SUPPRESSION DU COMPTE ==================== */}
          {/* Section Aide et Tutoriels */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gold-500/20 flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-gold-500" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-white">Aide et tutoriels</h2>
                <p className="text-zinc-500 text-sm">Gérez vos préférences d'affichage</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Toggle tutoriels */}
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-gold-500" />
                  <div>
                    <p className="text-white font-medium">Afficher les tutoriels</p>
                    <p className="text-zinc-500 text-sm">
                      Léon vous guidera dans chaque module de l'application
                    </p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    setUpdatingTutorials(true);
                    const success = await setTutorialsEnabled(!tutorialsEnabled);
                    if (success) {
                      toast.success(
                        tutorialsEnabled 
                          ? 'Tutoriels masqués' 
                          : 'Tutoriels réactivés'
                      );
                    }
                    setUpdatingTutorials(false);
                  }}
                  disabled={updatingTutorials}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    tutorialsEnabled 
                      ? 'bg-gold-500' 
                      : 'bg-zinc-700'
                  }`}
                  data-testid="toggle-tutorials-btn"
                >
                  <motion.div
                    className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md"
                    animate={{ left: tutorialsEnabled ? '1.75rem' : '0.25rem' }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                </button>
              </div>

              {/* Info message */}
              {!tutorialsEnabled && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg"
                >
                  <p className="text-amber-400 text-sm">
                    Les tutoriels sont actuellement masqués. Réactivez-les pour bénéficier des conseils de Léon.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Delete Account Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-card p-6 border-red-500/20"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-white">Supprimer mon compte</h2>
                <p className="text-zinc-500 text-sm">Action irréversible</p>
              </div>
            </div>

            {!showDeleteConfirm ? (
              <>
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-zinc-300 text-sm">
                      La suppression de votre compte entraînera la perte définitive de toutes vos données : 
                      audits, estimations, recettes et historique. Cette action est irréversible.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowDeleteConfirm(true)}
                  variant="outline"
                  className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer mon compte
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                {deleteError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm">{deleteError}</p>
                  </div>
                )}
                
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 font-bold mb-2">⚠️ Attention</p>
                  <p className="text-zinc-300 text-sm">
                    Pour confirmer la suppression, entrez votre mot de passe et tapez <strong>SUPPRIMER</strong> ci-dessous.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Mot de passe</label>
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                    placeholder="Entrez votre mot de passe"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Tapez <span className="text-red-500 font-bold">SUPPRIMER</span> pour confirmer
                  </label>
                  <Input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                    placeholder="SUPPRIMER"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deleting || deleteConfirmText !== 'SUPPRIMER'}
                    className="bg-red-600 hover:bg-red-500 text-white"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Supprimer définitivement
                  </Button>
                  <Button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletePassword('');
                      setDeleteConfirmText('');
                      setDeleteError(null);
                    }}
                    variant="outline"
                    className="border-zinc-700 text-zinc-400"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
